import { fmtDateTime, titleCase } from "@/lib/api";
import { ShieldCheck, FileText, Clock, Cpu, UserCheck } from "lucide-react";

export function Rag({ level, label, testId }) {
  const l = ["green", "amber", "red"].includes(level) ? level : "neutral";
  const text = label ?? ({ green: "Green · Automate", amber: "Amber · AI prepares, human approves", red: "Red · Human decides", neutral: "Pending" }[l]);
  return (
    <span data-testid={testId || `rag-${l}`} className={`inline-flex items-center gap-1.5 border rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap rag-${l}`}>
      <span className={`h-1.5 w-1.5 rounded-full dot-${l}`} />
      {text}
    </span>
  );
}

const STATUS_RAG = { met: "green", confirmed: "green", issued: "green", approved: "green", granted: "green", completed: "green", reviewed: "green", agreed: "green", sent: "green", scheduled: "neutral",
  pending: "amber", due_soon: "amber", pending_approval: "amber", pending_review: "amber", awaiting_review: "amber", partially_agreed: "amber", simulated: "amber", waived: "amber", in_progress: "amber",
  overdue: "red", rejected: "red", refused: "red", disputed: "red", failed: "red", adjourned: "red", not_yet_required: "neutral", draft: "amber",
  non_compliance_notice: "amber", tribunal_book: "green", approved: "green", declined: "red", discarded: "neutral", queued: "amber", running: "amber", done: "green",
  recorded: "amber", orders_issued: "green", orders_refused: "red", settled: "green", joint_report_filed: "green",
  awaiting_party_confirmation: "amber", declined_by_party: "red", granted: "green", granted_in_part: "amber", refused: "red" };

export function Status({ value, testId }) {
  return <Rag level={STATUS_RAG[value] || "neutral"} label={titleCase(value)} testId={testId} />;
}

export function Panel({ title, action, children, className = "", testId }) {
  return (
    <section data-testid={testId} className={`panel ${className}`}>
      {title && <header className="panel-h"><h3 className="text-sm font-semibold text-slate-900">{title}</h3>{action}</header>}
      <div className="panel-b">{children}</div>
    </section>
  );
}

export function Stat({ label, value, sub, tone = "neutral", testId }) {
  const tones = { neutral: "text-slate-900", green: "text-emerald-700", amber: "text-amber-700", red: "text-rose-700", blue: "text-blue-900" };
  return (
    <div data-testid={testId} className="panel p-5">
      <div className="eyebrow">{label}</div>
      <div className={`text-3xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export function Empty({ text }) {
  return <div className="text-sm text-slate-500 py-8 text-center" data-testid="empty-state">{text}</div>;
}

export function Provenance({ out, compact = false }) {
  const conf = out.confidence == null ? null : Math.round(out.confidence * 100);
  const confCls = conf == null ? "text-slate-500" : conf >= 85 ? "text-emerald-700" : conf >= 65 ? "text-amber-700" : "text-rose-700";
  const sources = out.content?.sources || out.sources || [];
  return (
    <div className={`provenance ${compact ? "px-3 py-2" : "px-4 py-3"}`} data-testid="provenance-card">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /><span className="eyebrow !text-indigo-700">Sources</span><span className="mono font-medium">{sources.length || (out.source_doc_ids?.length ?? 0)} docs</span></span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /><span className="eyebrow !text-indigo-700">Confidence</span><span className={`mono font-semibold ${confCls}`}>{conf == null ? "n/a" : `${conf}%`}</span></span>
        <span className="inline-flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5" /><span className="eyebrow !text-indigo-700">Model</span><span className="mono">{out.model_version}</span></span>
        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /><span className="eyebrow !text-indigo-700">Generated</span><span className="mono">{fmtDateTime(out.generated_at || out.classified_at)}</span></span>
        <span className="inline-flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5" /><span className="eyebrow !text-indigo-700">Reviewed</span>
          <span className="mono">{out.reviewed_at ? `${fmtDateTime(out.reviewed_at)} · ${out.reviewer_name || out.reviewed_by || ""}` : "Awaiting human review"}</span></span>
      </div>
      {!compact && sources.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {sources.map((s, i) => <li key={i} className="text-[11px] bg-white/70 border border-indigo-200 rounded px-1.5 py-0.5 mono" title={s.relevance}>{s.tag} {s.title}</li>)}
        </ul>
      )}
    </div>
  );
}
